import React from "react";
import { createPortal } from "react-dom";
import { commitMutation, graphql, useFragment } from "react-relay";

import { faExclamationCircle, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import {
	faArrowAltCircleUp,
	faCaretDown,
	faCaretUp,
	faCheck,
	faSparkles,
	faSpinner,
	faSquare,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { memoize } from "lodash";
import { commitLocalUpdate, fetchQuery } from "relay-runtime";
import invariant from "tiny-invariant";

import { showFeatureModal } from "~/billing";
import { DismissibleElementsContext } from "~/common/DismissibleElements";
import { inVideocall } from "~/lib/browserExtension";
import { useIntervalUpdate, useToggle } from "~/lib/hooks";
import { hasOwnProperty } from "~/lib/js";
import { confirm } from "~/lib/prompts";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getFirstUserFacingError, inVideocallMode } from "~/lib/utils";
import { useGroupEmitter } from "~/lib/websocket";
import { environment } from "~/models";
import StreamSettingsModal from "~/stream/StreamSettings";
import { CallIcon, callName } from "~/stream/VideoCallButton";
import { Button, Tooltip, useModals } from "~/ui";
import { Menu, MenuButton, MenuItem, MenuItems, MenuPopover } from "~/ui/dropdown";
import { ButtonLink, HelpCenterIcon, HelpCenterLink, IconButton, Switch } from "~/ui/legacyFellowIndex";
import toast, { ToastActionButton } from "~/ui/toast";

import { BillingContext } from "../account/Billing/BillingContext";
import { setSchedulingMeetingAssistantConfigValue } from "../stream/StreamSettings";
import { ReactComponent as RecordIconSVG } from "./css/record-icon.svg";
import {
	useMeetingRecorderBillingEnabled,
	useMeetingRecorderEnabled,
	useSchedulingMeetingAssistantFlagOn,
} from "./featureFlag";
import OnAirIcon from "./OnAirIcon";
import {
	useAutoRefetchMeetingRecordings,
	useIsNarrowViewport,
	useIsReallyNarrowViewport,
	useIsSmallViewport,
} from "./utils";

import type {
	MeetingRecordingBanner_RecordMeetingButton_note$key,
	MeetingRecordingBanner_RecordMeetingButton_note$data as MeetingRecordingBannerQuery,
} from "~/__generated__/MeetingRecordingBanner_RecordMeetingButton_note.graphql";
import type { MeetingRecordingBanner_RecordMeetingMutation as MeetingRecordingBannerRecordMeetingMutation } from "~/__generated__/MeetingRecordingBanner_RecordMeetingMutation.graphql";
import type { MeetingRecordingBanner_StopMeetingRecordingMutation as MeetingRecordingBannerStopMeetingRecordingMutation } from "~/__generated__/MeetingRecordingBanner_StopMeetingRecordingMutation.graphql";
import type { MeetingRecordingBanner_viewer$key } from "~/__generated__/MeetingRecordingBanner_viewer.graphql";
import type { MeetingRecordingBannerCreditUpdater_user$key } from "~/__generated__/MeetingRecordingBannerCreditUpdater_user.graphql";
import type { ConferenceProviderOptions } from "~/__generated__/useConferencingOptions_viewer.graphql";

import styles from "./css/MeetingRecordingBanner.module.css";

type ConferenceCall = NonNullable<MeetingRecordingBannerQuery["event"]>["conferenceCalls"][number];
type WsMessage =
	| { type: "MEETING_RECORDER_STATUS_UPDATED"; data: { meetingRecordingId: string; status: string } }
	| { type: "something else" };
type ErrorStatus = { type: "error"; message: string };
type Status = "not recording" | "starting" | "waiting" | "stopping" | "recording" | "saved" | ErrorStatus;

function isErrorStatus(s: Status): s is ErrorStatus {
	return s && typeof s === "object" && s.type === "error";
}

const RECORDING_BANNER_EXTENSION_MOUNT_POINT_ID = "recording-banner-extension-mount-point";

// this list should match the list in the MeetingInfoView class in the backend.
const SUPPORTED_CALL_PROVIDERS: readonly ConferenceCall["provider"][] = ["HANGOUTS_MEET", "MS_TEAMS", "ZOOM"];
export const SUPPORTED_CALL_PROVIDERS_OPTIONS: readonly ConferenceProviderOptions[] = ["GOOGLE", "TEAMS", "ZOOM"];

const REMOTE_STATUS_MAPPING = {
	READY: "starting",
	IN_WAITING_ROOM: "waiting",
	JOINED_CALL: "starting",
	RECORDING: "recording",
	RECORDING_DONE: "not recording",
	KICKED_OUT: "not recording",
	JOIN_REQUEST_DENIED: { type: "error", message: "Note Taker denied entry" },
	ERROR: { type: "error", message: "Something went wrong…" },
	UNKNOWN: { type: "error", message: "Note Taker unavailable" },
	TIME_OUT_EXCEEDED_WAITING_ROOM: { type: "error", message: "Time in waiting room exceeded" },
	TIME_OUT_EXCEEDED_IN_CALL_NOT_RECORDING: {
		type: "error",
		message: "Note Taker denied recording permission",
	},
	TIME_OUT_EXCEEDED_RECORDING_PERMISSION_DENIED: {
		type: "error",
		message: "Note Taker denied recording permission",
	},
	RECORDING_PERMISSION_DENIED: {
		type: "error",
		message: "Note Taker denied recording permission",
	},
} as const satisfies Record<string, Status>;

type RecordingIconEvent = Pick<NonNullable<MeetingRecordingBannerQuery["event"]>, "end" | "status" | "conferenceCalls">;
export function shouldShowRecordingIcon(
	schedulingMeetingAssistantAutomationIsActive: boolean,
	event: RecordingIconEvent,
) {
	const now = Date.now();

	return (
		event.status !== "cancelled" &&
		schedulingMeetingAssistantAutomationIsActive &&
		anyConferenceCallSupported(event.conferenceCalls) &&
		!!event.end &&
		new Date(event.end).getTime() > now
	);
}

export function anyConferenceCallSupported(conferenceCalls: RecordingIconEvent["conferenceCalls"]) {
	return (
		!!conferenceCalls &&
		conferenceCalls.length > 0 &&
		conferenceCalls.some(call => SUPPORTED_CALL_PROVIDERS.includes(call.provider))
	);
}

export function MeetingRecordingBannerVideoCallMountPoint() {
	// The banner will be mounted inside this div using a portal when in video
	// call mode (e.g. the chrome extension).
	return <div id={RECORDING_BANNER_EXTENSION_MOUNT_POINT_ID} />;
}

export function RecordIcon({ className }: { className?: string }) {
	return <RecordIconSVG className={cx(styles.recordingButtonIcon, className)} />;
}

function notifyExtensionRecordingInProgress() {
	invariant(inVideocall);
	window.parent.postMessage({ type: "MEETING_RECORDER_STARTED" }, "*");
}

export function useMeetingRecordingCreditUpdater(userKey: MeetingRecordingBannerCreditUpdater_user$key) {
	const user = useFragment(
		graphql`
			fragment MeetingRecordingBannerCreditUpdater_user on User {
				id
			}
		`,
		userKey,
	);

	const socket = useGroupEmitter(`user.${user.id.replace(/=/g, "")}`);

	React.useEffect(() => {
		function onSocketMessage(event: { type: string; data: { remainingMeetingRecordingCredits: number } }) {
			if (event.type !== "REMAINING_MEETING_RECORDING_CREDITS_CHANGED") return;
			const { remainingMeetingRecordingCredits } = event.data;
			if (typeof remainingMeetingRecordingCredits !== "number") return;

			commitLocalUpdate(environment, store => {
				const viewer = store.get("viewer");
				if (!viewer) return;
				viewer.setValue(remainingMeetingRecordingCredits, "remainingMeetingRecordingCredits");
			});
		}

		socket.on("message", onSocketMessage);
		return () => socket.off("message", onSocketMessage);
	}, [socket]);
}

function refetchViewerInBackground() {
	fetchQuery(
		environment,
		graphql`
			query MeetingRecordingBannerRefetchViewerQuery {
				viewer {
					id
					...MeetingRecordingBanner_viewer
				}
			}
		`,
		{},
		{ fetchPolicy: "network-only" },
	).toPromise();
}

const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000;

function MeetingRecordingBanner({
	viewer: viewerKey,
	note: noteKey,
}: {
	viewer: MeetingRecordingBanner_viewer$key;
	note: MeetingRecordingBanner_RecordMeetingButton_note$key;
}) {
	const { analyticsFingerprint } = React.useContext(BillingContext);
	const viewer = useFragment(
		graphql`
			fragment MeetingRecordingBanner_viewer on Viewer {
				remainingMeetingRecordingCredits
				hasAccessToCopilot
				account {
					billingAccount {
						isSubscriptionActive
						hasAccessToMeetingRecorderFeature: hasAccessToFeature(feature: meeting_recorder)
					}
				}
			}
		`,
		viewerKey,
	);

	const note = useFragment(
		graphql`
			fragment MeetingRecordingBanner_RecordMeetingButton_note on Note {
				id
				groupName
				finished
				account {
					allowAiFeatures
				}
				meetingRecordings {
					id
					status
				}
				event {
					start
					end
					status
					conferenceCalls {
						provider
						url
					}
				}

				stream {
					id
					hasAnyAutomationActive
					schedulingMeetingAssistantAutomationIsActive
				}

				...utils_useAutoRefetchMeetingRecording_note
			}
		`,
		noteKey,
	);

	const latestMeetingRecording = note.meetingRecordings?.length
		? note.meetingRecordings[note.meetingRecordings.length - 1]
		: undefined;

	let initialStatus: Status;
	if (!latestMeetingRecording) {
		initialStatus = "not recording";
	} else {
		initialStatus = hasOwnProperty<typeof REMOTE_STATUS_MAPPING>(REMOTE_STATUS_MAPPING, latestMeetingRecording.status)
			? REMOTE_STATUS_MAPPING[latestMeetingRecording.status]
			: "not recording";
	}

	const recordedConferenceCallRef = React.useRef<ConferenceCall | null>(null);
	const initialStatusRef = React.useRef(initialStatus);
	React.useEffect(() => {
		if (initialStatusRef.current === "recording" && inVideocall) {
			notifyExtensionRecordingInProgress();
		}
	}, []);

	const [status, setStatus] = React.useState<Status>(initialStatus);

	useAutoRefetchMeetingRecordings(note);

	const socket = useGroupEmitter(note.groupName);

	const startRecording = React.useCallback(
		(call: ConferenceCall) => {
			setStatus("starting");
			commitMutation<MeetingRecordingBannerRecordMeetingMutation>(environment, {
				mutation: graphql`
					mutation MeetingRecordingBanner_RecordMeetingMutation($noteId: ID!, $meetingCallUrl: String!) {
						recordMeeting(noteId: $noteId, meetingCallUrl: $meetingCallUrl) {
							meetingRecording {
								id
								status
							}
						}
					}
				`,
				variables: {
					noteId: note.id,
					meetingCallUrl: call.url,
				},
				onCompleted: (data, errors) => {
					const recordingStatus = data.recordMeeting?.meetingRecording?.status;

					if (errors && errors.length > 0) {
						const userFacingError = getFirstUserFacingError(errors);
						if (userFacingError) {
							setStatus({ type: "error", message: userFacingError });
						} else {
							setStatus(REMOTE_STATUS_MAPPING.UNKNOWN);
						}
						// The user may have used up their credits in another session
						// while the websocket was disconnected and the data here has
						// become stale.
						refetchViewerInBackground();
					} else if (recordingStatus) {
						const mappedRecordingStatus = hasOwnProperty<typeof REMOTE_STATUS_MAPPING>(
							REMOTE_STATUS_MAPPING,
							recordingStatus,
						)
							? REMOTE_STATUS_MAPPING[recordingStatus]
							: REMOTE_STATUS_MAPPING.UNKNOWN;
						if (mappedRecordingStatus) setStatus(mappedRecordingStatus);
						recordedConferenceCallRef.current = call;
						dispatchAnalyticsTrack("[AI Copilot] Meeting Recording Started", {
							fingerprint: analyticsFingerprint,
							meeting_provider: call.provider, // eslint-disable-line camelcase
						});
					}
				},
			});
		},
		[note, analyticsFingerprint],
	);

	const stopRecording = React.useCallback(() => {
		setStatus("stopping");
		commitMutation<MeetingRecordingBannerStopMeetingRecordingMutation>(environment, {
			mutation: graphql`
				mutation MeetingRecordingBanner_StopMeetingRecordingMutation($noteId: ID!) {
					stopMeetingRecording(noteId: $noteId) {
						note {
							id
							hasMeetingRecordings
							meetingRecordings {
								id
								isCurrentlyRecording
							}
						}
					}
				}
			`,
			variables: {
				noteId: note.id,
			},
		});
	}, [note]);

	const onStopRecordingClick = React.useCallback(async () => {
		const confirmText = <p>The meeting recording and transcript up until this point will be saved.</p>;
		if (
			!(await confirm(confirmText, {
				yesText: "Stop",
				yesProps: { variant: "primary" },
				title: "Stop recording?",
			}))
		) {
			return;
		}
		stopRecording();
		dispatchAnalyticsTrack("[AI Copilot] Meeting Recording Stopped", {
			fingerprint: analyticsFingerprint,
			meeting_provider: recordedConferenceCallRef.current?.provider, // eslint-disable-line camelcase
		});
	}, [stopRecording, analyticsFingerprint]);

	React.useEffect(() => {
		function onSocketMessage(message: WsMessage) {
			if (message.type === "MEETING_RECORDER_STATUS_UPDATED") {
				const newStatus =
					message.data.status === "RECORDING_DONE"
						? "saved"
						: hasOwnProperty(REMOTE_STATUS_MAPPING, message.data.status)
							? REMOTE_STATUS_MAPPING[message.data.status]
							: undefined;
				if (newStatus) {
					if (newStatus === "recording" && inVideocall) {
						notifyExtensionRecordingInProgress();
					}
					// Handle out of order JOINED_CALL and RECORDING messages
					setStatus(oldStatus => {
						if (oldStatus === "recording" && newStatus === "starting") return "recording";
						return newStatus;
					});
				}
			}
		}

		socket.on("message", onSocketMessage);

		return () => {
			socket.off("message", onSocketMessage);
		};
	}, [socket, latestMeetingRecording, note]);

	// Automatically switch from "saved" to "not recording" after 5 seconds
	// Automatically switch from "error" to "not recording" after 5 seconds
	React.useEffect(() => {
		if (status !== "saved" && !isErrorStatus(status)) return;
		const timeout = window.setTimeout(() => setStatus("not recording"), 5000);
		return () => window.clearTimeout(timeout);
	}, [status]);

	const now = Date.now();
	const meetingStartingSoon =
		!!note.event?.start && new Date(note.event.start).getTime() - 5 * ONE_MINUTE_IN_MILLISECONDS < now;

	const meetingInProgress =
		!!note.event?.start &&
		!!note.event?.end &&
		new Date(note.event.start).getTime() <= now &&
		now <= new Date(note.event.end).getTime();

	const schedulingMeetingAssistantFlagOn = useSchedulingMeetingAssistantFlagOn();
	const schedulingAutomationIsActive = note.stream.schedulingMeetingAssistantAutomationIsActive;

	// We want to avoid removing the banner as soon as the user toggles on
	// So we use this to show it in the UI until the user refreshes the page
	const [initialSchedulingMeetingAssistantAutomationIsActiveValue, _] = React.useState(schedulingAutomationIsActive);

	const automationSettingChangedRecently =
		schedulingAutomationIsActive !== initialSchedulingMeetingAssistantAutomationIsActiveValue;

	// The schedule version has a toggle instead of a button and uses automations behind the scenes
	const showScheduleVersion =
		schedulingMeetingAssistantFlagOn &&
		!meetingInProgress &&
		!meetingStartingSoon &&
		(!schedulingAutomationIsActive || automationSettingChangedRecently);

	const isTimely =
		!note.finished &&
		!!note.event?.end &&
		now <= new Date(note.event.end).getTime() &&
		note.event?.status !== "cancelled" &&
		(showScheduleVersion || (schedulingAutomationIsActive ? meetingInProgress : meetingStartingSoon));

	useIntervalUpdate(ONE_MINUTE_IN_MILLISECONDS);

	const featureEnabled = useMeetingRecorderEnabled();
	const isNarrowViewport = useIsNarrowViewport();
	const isSmallViewport = useIsSmallViewport();
	const isReallyNarrowViewport = useIsReallyNarrowViewport();
	const conferenceCalls = note.event?.conferenceCalls;
	const anyConferenceCallSupported = React.useMemo(
		() => conferenceCalls?.some(call => SUPPORTED_CALL_PROVIDERS.includes(call.provider)),
		[conferenceCalls],
	);

	const videoCallMountPoint = React.useMemo(
		() => document.getElementById(RECORDING_BANNER_EXTENSION_MOUNT_POINT_ID),
		[],
	);

	const { dismiss, dismissedElements } = React.useContext(DismissibleElementsContext);
	const isOnFreePlan = !viewer.account.billingAccount.isSubscriptionActive;
	const permanentlyDismissedPaywalledBanner =
		!isOnFreePlan &&
		dismissedElements.some(el => el.key == "MeetingRecordingBanner__paywalledBanner" && el.dismissedCount >= 3);
	const billingEnabled = useMeetingRecorderBillingEnabled();
	const [temporarilyDismissedPaywalledBanner, temporarilyDismissPaywalledBanner] = useToggle();

	const dismissPaywalledBanner = React.useCallback(() => {
		temporarilyDismissPaywalledBanner();
		if (!isOnFreePlan) dismiss("MeetingRecordingBanner__paywalledBanner");
	}, [temporarilyDismissPaywalledBanner, dismiss, isOnFreePlan]);

	const showPaywall = React.useCallback(() => {
		showFeatureModal("meeting_recorder");
	}, []);

	if (
		!featureEnabled ||
		!conferenceCalls ||
		conferenceCalls.length === 0 ||
		!anyConferenceCallSupported ||
		(!isTimely && status !== "recording") ||
		!viewer.hasAccessToCopilot
	)
		return null;

	let icon, text, remainingRecordingsButton, remainingRecordingsText, button, dismissButton;
	let bannerColorClass = styles.bannerSuccess;

	if (status === "not recording") {
		const accountHasAddon = viewer.account.billingAccount.hasAccessToMeetingRecorderFeature;
		const { remainingMeetingRecordingCredits } = viewer;
		const canRecord = !billingEnabled || accountHasAddon || remainingMeetingRecordingCredits > 0;

		icon =
			showScheduleVersion && isReallyNarrowViewport ? null : (
				<FontAwesomeIcon icon={faSparkles} fixedWidth className={styles.purpleIcon} />
			);

		text = (
			<>
				{showScheduleVersion
					? `Auto-record and summarize${isSmallViewport ? "." : " this meeting."}`
					: "Record this meeting."}
				<div className={styles.helpCenterLinkContainer}>
					{!isNarrowViewport ? (
						<HelpCenterLink articleId="8589106">Learn more</HelpCenterLink>
					) : (
						<HelpCenterIcon articleId="8589106" title="Learn more" />
					)}
				</div>
			</>
		);

		if (canRecord) {
			remainingRecordingsText =
				!billingEnabled ||
				accountHasAddon ||
				remainingMeetingRecordingCredits === 0 ||
				remainingMeetingRecordingCredits > 2
					? undefined
					: `${remainingMeetingRecordingCredits} recording${remainingMeetingRecordingCredits === 1 ? "" : "s"} left`;

			remainingRecordingsButton = remainingRecordingsText && !isSmallViewport && (
				<Button variant="tertiary" size="slim" className={styles.remainingRecordingsButton} onClick={showPaywall}>
					{remainingRecordingsText}
				</Button>
			);

			const buttonInner = showScheduleVersion ? (
				// Allowing users schedule bots
				<SchedulingToggle
					streamId={note.stream.id}
					value={schedulingAutomationIsActive}
					showText={!isSmallViewport && !remainingRecordingsButton}
				/>
			) : (
				// Allowing users create adhoc bots
				<StartRecordingButton startRecording={startRecording} conferenceCalls={conferenceCalls} disabled={!canRecord} />
			);

			if (remainingRecordingsText && !remainingRecordingsButton) {
				// Tooltip is visible when there's remaining recordings text and no remaining recordings button.
				button = <Tooltip content={remainingRecordingsText}>{buttonInner}</Tooltip>;
			} else {
				const tooltipContent = (
					<div className={styles.tooltipContent}>
						{showScheduleVersion ? (
							<>
								<h1 className={styles.title}>Auto-recording is {schedulingAutomationIsActive ? "on" : "off"}</h1>
								<p className={styles.text}>
									{schedulingAutomationIsActive ? "The" : "When enabled, the"} Note Taker will automatically request to
									join the call to record and take AI notes for you.
								</p>
							</>
						) : (
							<p className={styles.text}>
								The Note Taker will request to join the call to record and take AI notes for this meeting.
							</p>
						)}
					</div>
				);

				button = (
					<Tooltip content={tooltipContent} placement="bottom">
						{buttonInner}
					</Tooltip>
				);
			}
		} else if (!temporarilyDismissedPaywalledBanner && !permanentlyDismissedPaywalledBanner) {
			button = showScheduleVersion ? (
				<SchedulingToggle
					streamId={note.stream.id}
					value={note.stream.schedulingMeetingAssistantAutomationIsActive}
					showText={!isSmallViewport && !remainingRecordingsButton}
					onShowPaywall={showPaywall}
				/>
			) : (
				<Button
					size="slim"
					variant="billing"
					className={cx(styles.recordingButton, styles.paywalledRecordingButton)}
					onClick={showPaywall}
				>
					<FontAwesomeIcon icon={faArrowAltCircleUp} className={styles.crownIcon} />
					Record
				</Button>
			);
			dismissButton = (
				<IconButton icon={faTimes} onClick={dismissPaywalledBanner} className={styles.paywalledBannerDismissButton} />
			);
		} else {
			return null;
		}
	} else if (status === "starting") {
		icon = <FontAwesomeIcon icon={faSpinner} fixedWidth pulse />;
		text = <>Note Taker joining…</>;
		button = null;
	} else if (status === "waiting") {
		icon = <FontAwesomeIcon icon={faSpinner} fixedWidth pulse />;
		text = <>Note Taker waiting to be let in…</>;
		button = null;
	} else if (status === "recording") {
		icon = <OnAirIcon className={styles.onAirIcon} />;
		text = <>Recording in progress</>;
		button = (
			<Button
				variant="primary-ai"
				onClick={onStopRecordingClick}
				size="slim"
				className={styles.recordingButton}
				title="Stop recording"
			>
				{isReallyNarrowViewport || (
					<FontAwesomeIcon icon={faSquare} fixedWidth className={styles.recordingButtonIcon} />
				)}
				Stop
			</Button>
		);
	} else if (status === "stopping") {
		icon = <FontAwesomeIcon icon={faSpinner} fixedWidth pulse />;
		text = <>Saving recording…</>;
		button = null;
	} else if (status === "saved") {
		icon = <FontAwesomeIcon icon={faCheck} fixedWidth className={styles.greenIcon} />;
		text = <>Recording saved</>;
		button = null;
	} else if (isErrorStatus(status)) {
		bannerColorClass = styles.bannerError;
		icon = <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />;
		text = <>{status.message}</>;
		button = null;
	} else {
		// TODO: Error state, for example if bot is denied entry
		return null;
	}

	let innerBannerContent = (
		<div className={cx(styles.banner, bannerColorClass)}>
			{icon && <span className={styles.iconContainer}>{icon}</span>}
			<span className={styles.textContainer}>{text}</span>
			{remainingRecordingsButton}
			{button}
			{dismissButton}
		</div>
	);

	return inVideocallMode
		? videoCallMountPoint && createPortal(innerBannerContent, videoCallMountPoint)
		: innerBannerContent;
}

interface SchedulingToggleProps {
	streamId: string;
	value: boolean;
	showText?: boolean;
	onShowPaywall?: () => void;
}

const SchedulingToggle = React.forwardRef<HTMLDivElement, SchedulingToggleProps>(function SchedulingToggle(
	{ streamId, value, showText, onShowPaywall },
	ref,
) {
	const modalCtx = useModals();
	const onOpenSettings = React.useCallback(() => {
		modalCtx?.open(({ close }) => (
			<StreamSettingsModal close={close} streamId={streamId} defaultSection="automations" />
		));
	}, [modalCtx, streamId]);

	const onUndo = React.useCallback(() => setSchedulingMeetingAssistantConfigValue(streamId, value), [streamId, value]);
	const onSuccess = React.useCallback(
		(v: boolean) => {
			const title = `Note Taker ${v ? "successfully added" : "removed"}`;
			const message = (
				<p>
					Meetings in this series will {v ? "now" : "no longer"} be auto-recorded and summarized. You can change this in
					<ButtonLink className={styles.settingsLink} onClick={onOpenSettings}>
						Automation settings
					</ButtonLink>
					.
				</p>
			);

			toast.success(message, {
				title,
				closeButton: true,
				actions: (
					<ToastActionButton variant="secondary" onClick={onUndo}>
						Undo
					</ToastActionButton>
				),
			});
		},
		[onOpenSettings, onUndo],
	);

	const onChange = React.useCallback(
		async () => onSuccess(await setSchedulingMeetingAssistantConfigValue(streamId, !value)),
		[streamId, onSuccess, value],
	);
	const text = value ? "On" : "Off";
	return (
		<div className={styles.schedulingToggleContainer} ref={ref}>
			{onShowPaywall ? (
				<FontAwesomeIcon icon={faArrowAltCircleUp} className={styles.yellowCrownIcon} onClick={onShowPaywall} />
			) : (
				showText && text
			)}
			<Switch className={styles.toggle} checked={value} size="small" onCheckedChange={onShowPaywall || onChange} />
		</div>
	);
});

type StartRecordingButtonProps = {
	conferenceCalls: readonly ConferenceCall[];
	startRecording: (call: ConferenceCall) => void;
	disabled?: boolean;
};

const StartRecordingButton = React.forwardRef<HTMLButtonElement, StartRecordingButtonProps>(
	function StartRecordingButton({ conferenceCalls, startRecording, disabled }, ref) {
		const isReallyNarrowViewport = useIsReallyNarrowViewport();
		const makeStartRecording = React.useMemo(() => memoize(call => () => startRecording(call)), [startRecording]);

		if (conferenceCalls.length === 1) {
			return (
				<Button
					variant="primary-ai"
					size="slim"
					className={styles.recordingButton}
					onClick={makeStartRecording(conferenceCalls[0])}
					disabled={disabled}
					ref={ref}
				>
					{isReallyNarrowViewport || <RecordIcon />}
					Record
				</Button>
			);
		} else if (conferenceCalls.length > 1) {
			return (
				<Menu>
					{({ isExpanded }) => (
						<>
							<MenuButton
								className={cx(styles.startRecordingMenuButton, { [styles.open]: isExpanded })}
								as={Button}
								variant="primary-ai"
								size="slim"
								disabled={disabled}
								ref={ref}
							>
								{isReallyNarrowViewport || <RecordIcon />}
								<span>Record</span>
								<FontAwesomeIcon icon={isExpanded ? faCaretUp : faCaretDown} className={styles.caretIcon} />
							</MenuButton>
							<MenuPopover>
								<MenuItems className={styles.startRecordingButtonMenuItems}>
									<p className={styles.startRecordingButtonPlaceholder}>Select a call to record</p>
									{conferenceCalls.map(call => {
										const providerSupported = SUPPORTED_CALL_PROVIDERS.includes(call.provider);
										return (
											<MenuItem
												key={call.url}
												onSelect={makeStartRecording(call)}
												className={styles.startRecordingMenuButton}
												disabled={!providerSupported}
											>
												<Tooltip content="This call provider is not supported" disabled={providerSupported}>
													<div>
														<CallIcon provider={call.provider} />
														<p className={styles.callProviderName}>{callName(call.provider)}</p>
													</div>
												</Tooltip>
											</MenuItem>
										);
									})}
								</MenuItems>
							</MenuPopover>
						</>
					)}
				</Menu>
			);
		} else {
			return null;
		}
	},
);

export default MeetingRecordingBanner;
